import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const KeepForeverAnimation: React.FC = () => {
  const cardRef = useRef<SVGGElement>(null);
  const toyRef = useRef<SVGGElement>(null);
  const heartRef = useRef<SVGGElement>(null);
  const tagRef = useRef<SVGGElement>(null);
  const checkRef = useRef<SVGGElement>(null);

  useEffect(() => {
    const tl = gsap.timeline({ repeat: -1, repeatDelay: 0.6 });

    // Initial state
    tl.set(cardRef.current, { opacity: 1, scale: 1 });
    tl.set(toyRef.current, { opacity: 1, y: 0, scale: 1 });
    tl.set(heartRef.current, {
      opacity: 0,
      scale: 0,
      transformOrigin: "400px 330px",
    }); // center of heart
    tl.set(tagRef.current, {
      opacity: 0,
      transformOrigin: "510px 260px", // top-right corner pivot
      rotation: -20,
    });
    tl.set(checkRef.current, {
      opacity: 0,
      scale: 0,
      transformOrigin: "510px 260px",
    });

    // Subtle card pop-in
    tl.from(
      cardRef.current,
      { scale: 0.98, duration: 0.4, ease: "power2.out" },
      0
    );

    // Heart appears & pulses
    tl.to(
      heartRef.current,
      { opacity: 1, scale: 1, duration: 0.35, ease: "back.out(2)" },
      0.15
    );
    tl.to(
      heartRef.current,
      { scale: 1.06, duration: 0.5, yoyo: true, repeat: 2, ease: "sine.inOut" },
      ">-0.05"
    );

    // Tag swings in
    tl.to(tagRef.current, { opacity: 1, duration: 0.15 }, ">-0.2");
    tl.to(
      tagRef.current,
      { rotation: 10, duration: 0.35, ease: "sine.out" },
      "<"
    );
    tl.to(tagRef.current, { rotation: -6, duration: 0.35, ease: "sine.inOut" });
    tl.to(tagRef.current, { rotation: 0, duration: 0.25, ease: "sine.inOut" });

    // Swap tag -> check badge (“Yours”)
    tl.to(tagRef.current, { opacity: 0, duration: 0.15 }, ">0.1");
    tl.to(
      checkRef.current,
      { opacity: 1, scale: 1, duration: 0.25, ease: "back.out(2)" },
      "<"
    );

    // Hold moment
    tl.to({}, { duration: 0.9 });

    // Fade out for reset
    tl.to(
      [heartRef.current, checkRef.current, toyRef.current, cardRef.current],
      {
        opacity: 0,
        duration: 0.35,
        ease: "power1.out",
      }
    );

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <svg
      viewBox="0 0 800 600"
      className="w-full h-auto"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Keep the toy forever"
    >
      {/* Background */}
      <rect width="800" height="600" fill="#fff7ed" />

      {/* Card */}
      <g ref={cardRef}>
        <rect x="220" y="180" width="360" height="260" rx="28" fill="#fff7ed" />
      </g>

      {/* Heart behind toy */}
      <g ref={heartRef}>
        <path
          d="M415 280
             c -28 -40 -96 -18 -96 28
             c 0 54 72 98 96 112
             c 24 -14 96 -58 96 -112
             c 0 -46 -68 -68 -96 -28z"
          fill="#ef4444"
          opacity="0.9"
        />
      </g>

      {/* Minimal toy: teddy head (reads as a toy at a glance) */}
      <g ref={toyRef}>
        {/* ears */}
        <circle cx="344" cy="280" r="28" fill="#a16207" />
        <circle cx="456" cy="280" r="28" fill="#a16207" />
        {/* head */}
        <circle cx="400" cy="320" r="72" fill="#f59e0b" />
        {/* snout */}
        <ellipse cx="400" cy="340" rx="34" ry="24" fill="#fde68a" />
        {/* eyes */}
        <circle cx="376" cy="312" r="6" fill="#111827" />
        <circle cx="424" cy="312" r="6" fill="#111827" />
        {/* nose */}
        <circle cx="400" cy="334" r="5" fill="#111827" />
      </g>
    </svg>
  );
};

export default KeepForeverAnimation;
